import { Injectable } from '@angular/core'
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms'
import { PersonaTypes } from '@personas/enums/persona-types.enum'
import { _HANDSHAKE_VERSION, HandshakeStatus } from '@personas/interfaces/handshake-status.interface'
import { DEFAULT_HANDSHAKE_SCHEMA_V2, HandshakeSchemaV1, HandshakeSchemaV2 } from '@personas/interfaces/handshake.interface'
import { Persona, Personas } from '@personas/interfaces/persona.interface'
import { Subject } from 'rxjs'
import { HandshakeTransformException } from './handshake-transform.exception'
/**
 * Handshake factory service handles creating/ manipulating handshak schemas
 */
@Injectable({
  providedIn: 'root'
})
export class HandshakesFactoryService {
  /**
   * Current default handshake schema version
   */
  private DefaultSchema = DEFAULT_HANDSHAKE_SCHEMA_V2

  /**
   * Subject to emit new handshake values when they change
   */
  handshakeSubject$: Subject<UntypedFormGroup> = new Subject<UntypedFormGroup>()

  /**
   * Transforms a handshake schema iteratively until a certain version {target} is reach - used for fallbacks
   * @param schema HandshakeSchemaV1 | HandshakeSchemaV2
   * @param current number
   * @param target number
   * @returns HandshakeSchemaV{target}
   */
  private transformVersion(schema: HandshakeSchemaV1 | HandshakeSchemaV2, current: number, target: number) {
    try {
      if (current === _HANDSHAKE_VERSION) {
        return schema
      }
      return current === target - 1 ? this['_transformV' + current.toString() + 'V' + (current + 1).toString()](schema) : this.transformVersion(schema, current, target - 1)
    } catch (exception) {
      throw new HandshakeTransformException(schema, current, target, 'Invalid schema: ' + exception)
    }
  }

  /**
   * Initialize handshake schema as option metadata for a persona
   * @param personaGroup FormGroup
   */
  public initializeForPersona(personaGroup: UntypedFormGroup) {
    const persona: Persona = personaGroup.value as Persona
    personaGroup.controls.options.setValue({
      ...persona.options,
      handshakes:
        persona.options?.handshakes?.version === _HANDSHAKE_VERSION
          ? persona.options.handshakes
          : !persona.options?.handshakes
          ? this.DefaultSchema
          : this.transformVersion(persona.options.handshakes, persona.options.handshakes.version, _HANDSHAKE_VERSION)
    })
  }
  /**
   * Update handshake status for a persona
   * @param personaGroup FormGroup
   * @param status HandshakeStatus
   */
  public setStatus(personaGroup: UntypedFormGroup, status: HandshakeStatus) {
    const persona: Persona = personaGroup.value as Persona
    personaGroup.controls.options.setValue({
      ...persona.options,
      handshakes: {
        ...persona.options.handshakes,
        status: status
      }
    })
  }
  /**
   * Gets a list of interactions between the current persona and other event personas
   * @param personasArray FormArray
   * @param personaGroup FormGroup
   * @returns {slug: string; name: string; active: string}[]
   */
  public getInteractions(personasArray: UntypedFormArray, personaGroup: UntypedFormGroup) {
    return (personasArray.value as Personas)
      .filter((o) => o.type !== PersonaTypes.GENERIC)
      .map((persona) => {
        const personaInteractions = (personaGroup.value as Persona)?.options?.handshakes?.interactions
        return { slug: persona.slug, name: persona.name ? persona.name : 'New persona', active: personaInteractions ? personaInteractions.includes(persona.slug) : false }
      })
  }
  /**
   * Enables an interaction between current persona and the persona slug provided
   * @param personaGroup FormGroup
   * @param slug persona slug
   * @returns void
   */
  public toggleInteraction(personaGroup: UntypedFormGroup, slug) {
    const persona = personaGroup.value as Persona
    if (!persona.options.handshakes.interactions) {
      persona.options.handshakes.interactions = []
      personaGroup['controls']['options'].setValue(persona.options)
    }
    const hasInteraction = persona.options.handshakes?.interactions?.includes(slug)
    personaGroup.controls.options.setValue({
      ...persona.options,
      handshakes: {
        ...persona.options.handshakes,
        interactions: hasInteraction ? persona.options.handshakes.interactions.filter((o) => o !== slug) : [...(persona.options?.handshakes?.interactions || []), slug]
      }
    })
    personaGroup.markAsTouched()
    this.handshakeSubject$.next(personaGroup)
  }
  /**
   * Determines whether a persona has a handshake schema
   * @param personaGroup FormGroup
   * @returns boolean
   */
  public hasHandshake(personaGroup: UntypedFormGroup) {
    const persona = personaGroup.value as Persona
    return persona.options?.handshakes?.status === HandshakeStatus.ENABLED
  }
  /**
   * Adds a coordinator to a persona handshake metadata
   * @param personaGroup FormGroup
   */
  public addCoordinator(personaGroup: UntypedFormGroup) {
    const persona = personaGroup.value as Persona
    const handshakes = (persona.options.handshakes as HandshakeSchemaV2) || null
    personaGroup.controls.options.setValue({
      ...persona.options,
      handshakes: {
        ...persona.options.handshakes,
        coordinators: handshakes ? [...handshakes.coordinators, ''] : ['']
      }
    })
    personaGroup.markAsTouched()
  }
  /**
   * Deletes a coordinator from a persona handshake metadata
   * @param personaGroup FormGroup
   * @param index number
   */
  public deleteCoordinator(personaGroup: UntypedFormGroup, index: number) {
    const persona = personaGroup.value as Persona
    const handshakes = persona.options.handshakes as HandshakeSchemaV2
    handshakes.coordinators.splice(index, 1)
    personaGroup.controls.options.setValue({
      ...persona.options,
      handshakes: {
        ...persona.options.handshakes,
        coordinators: [...handshakes.coordinators]
      }
    })
    personaGroup.markAsTouched()
  }
  /**
   * Updates a coordinator in a persona handshake metadata
   * @param personaGroup FormGroup
   * @param index number
   * @param value string
   */
  public updateCoordinator(personaGroup: UntypedFormGroup, index: number, value: string) {
    const persona = personaGroup.value as Persona
    const handshakes = persona.options.handshakes as HandshakeSchemaV2
    handshakes.coordinators[index] = value
    personaGroup.controls.options.setValue({
      ...persona.options,
      handshakes: {
        ...persona.options.handshakes,
        coordinators: [...handshakes.coordinators]
      }
    })
  }
}

export interface interaction {
  slug: string
  name: string
  active: boolean
}
